import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { LocalCsvService } from '../../../../../services/local-csv/local-csv.service';
import { ModalFormContainerComponent } from '../../../../ui/modal-form-container/modal-form-container.component';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { validPhoneNumberValidator } from '../../../../../../utils/utils';
import { StatusComponent } from '../../../../ui/status/status.component';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputFieldComponent } from '../../../../ui/inputField/inputField.component';
import { COUNTRIES, CountryInfo } from '../../../../../types/ussd.models';
import type { CountryCode } from 'libphonenumber-js';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { ToastService } from '../../../../../services/toast/toast.service';

@Component({
  selector: 'list-csv-phone-numbers',
  standalone: true,
  imports: [
    ModalFormContainerComponent,
    NgxMatInputTelComponent,
    StatusComponent,
    ReactiveFormsModule,
    FormsModule,
    InputFieldComponent,
    NgClickOutsideDirective,
  ],
  templateUrl: './list-csv-phone-numbers.component.html',
  styleUrl: './list-csv-phone-numbers.component.css',
})
export class ListCsvPhoneNumbersComponent {
  constructor(private builder: FormBuilder) {}

  localCSVService = inject(LocalCsvService);
  private toast = inject(ToastService);

  @ViewChild('countryBox') private countryBox?: ElementRef<HTMLElement>;
  @ViewChild('countryTrigger') private countryTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('countryMenu') private countryMenu?: ElementRef<HTMLElement>;
  @ViewChild('countrySearchInput') private countrySearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('numberList') private numberList?: ElementRef<HTMLElement>;

  get phoneNumbers() {
    return this.localCSVService.getFormattedData;
  }

  // Country selection ---------------------------------------------------------
  // Numbers written in national format (commonly with a leading 0) are invalid
  // on their own. Picking a country re-parses them against that country's code
  // so e.g. a Ghana number `0241234567` becomes `+233241234567`.
  countries: CountryInfo[] = [...COUNTRIES].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  selectedCountry: string = this.localCSVService.getDefaultCountry ?? '';

  countryDropdownOpen = false;
  countrySearch = '';

  get selectedCountryInfo(): CountryInfo | undefined {
    return this.countries.find((c) => c.code === this.selectedCountry);
  }

  get filteredCountries(): CountryInfo[] {
    const term = this.countrySearch.trim().toLowerCase();
    if (!term) return this.countries;
    return this.countries.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.code.toLowerCase().includes(term) ||
        c.dialCode.replace('+', '').includes(term.replace('+', ''))
    );
  }

  toggleCountryDropdown() {
    this.countryDropdownOpen = !this.countryDropdownOpen;
    if (this.countryDropdownOpen) {
      this.countrySearch = '';
      // The menu renders on the next change detection; then start in the search.
      setTimeout(() => this.countrySearchInput?.nativeElement.focus());
    }
  }

  /** `returnFocus` puts keyboard users back on the trigger. */
  closeCountryDropdown(returnFocus: boolean = false) {
    if (!this.countryDropdownOpen) return;
    this.countryDropdownOpen = false;
    if (returnFocus) this.countryTrigger?.nativeElement.focus();
  }

  selectCountry(code: string) {
    this.selectedCountry = code;
    this.closeCountryDropdown(true);
    this.onCountryChange();
  }

  /**
   * Escape closes the open menu only. preventDefault tells the surrounding
   * dialog the key is handled, so it stays open. With the menu closed,
   * Escape goes on to close the dialog as usual.
   */
  onCountryEscape(event: Event) {
    if (!this.countryDropdownOpen) return;
    event.preventDefault();
    this.closeCountryDropdown(true);
  }

  /** Tabbing out of the picker closes the menu. */
  onCountryFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null;
    if (next && !this.countryBox?.nativeElement.contains(next)) this.closeCountryDropdown();
  }

  /** ArrowDown from the search enters the list; arrows, Home and End move through it. */
  onCountryMenuKeydown(event: KeyboardEvent) {
    const options = this.countryOptions();
    if (!options.length) return;
    const search = this.countrySearchInput?.nativeElement;
    const current = options.indexOf(document.activeElement as HTMLElement);
    let next = -1;
    switch (event.key) {
      case 'ArrowDown':
        next = current < 0 ? 0 : Math.min(current + 1, options.length - 1);
        break;
      case 'ArrowUp':
        if (current < 0) return;
        if (current === 0) {
          event.preventDefault();
          search?.focus();
          return;
        }
        next = current - 1;
        break;
      case 'Home':
        if (current < 0) return; // in the search, Home moves the caret
        next = 0;
        break;
      case 'End':
        if (current < 0) return;
        next = options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    options[next].focus();
  }

  private countryOptions(): HTMLElement[] {
    const menu = this.countryMenu?.nativeElement;
    return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="option"]')) : [];
  }

  /**
   * Remove a number. When the remove button had keyboard focus and the row
   * goes away, focus moves to the next number's remove button (or the one
   * before, or the country picker when the list is empty).
   */
  removeNumber(id: string, index: number) {
    const list = this.numberList?.nativeElement;
    const hadFocus = !!list && list.contains(document.activeElement);
    this.localCSVService.removeElementFromFormattedData(id);
    if (!hadFocus) return;
    setTimeout(() => {
      const buttons = this.numberList?.nativeElement.querySelectorAll<HTMLElement>('.lst__remove');
      const target = buttons?.length ? buttons[Math.min(index, buttons.length - 1)] : undefined;
      (target ?? this.countryTrigger?.nativeElement)?.focus();
    });
  }

  onCountryChange() {
    this.localCSVService.applyDefaultCountryAndRegenerate(
      (this.selectedCountry || undefined) as CountryCode | undefined
    );
  }

  get validCount(): number {
    return this.phoneNumbers.filter((p) => p.isValid !== false).length;
  }

  get invalidCount(): number {
    return this.phoneNumbers.filter((p) => p.isValid === false).length;
  }

  // code related to updating of phone Numbers-----------------------------------------------------------------------------------------------------
  // phone number form manipulations
  showUpdateModal: boolean = false;
  openUpdateModal(selectedPhoneNumber: {
    phoneNumber: string;
    id: string;
    country?: string | undefined;
  }) {
    this.selectedPhoneNumber = selectedPhoneNumber;
    this.phoneNumberForm.reset({
      phoneNumber: selectedPhoneNumber.phoneNumber,
    });
    this.showUpdateModal = true;
  }
  closeUpdateModal() {
    this.showUpdateModal = false;
  }

  phoneNumberForm = this.builder.group({
    phoneNumber: ['', [Validators.required, validPhoneNumberValidator]],
  });
  phoneNumberSubmitted: boolean = false;
  /** The last "not valid" toast, cleared by the next edit. */
  private errorToastId = 0;

  selectedPhoneNumber: {
    phoneNumber: string;
    id: string;
    country?: string | undefined;
  } | null = null;

  updatePhoneNumber() {
    this.phoneNumberSubmitted = true;
    // console.log('phonenumberForm', this.phoneNumberForm.get('phoneNumber'));
    this.phoneNumberForm.get('phoneNumber');
    if (this.phoneNumberForm.valid && this.phoneNumberSubmitted) {
      const phoneNumber = this.phoneNumberForm.value.phoneNumber;
      // this is where the phone number in the local csv is updated
      const selectedNumber = this.selectedPhoneNumber?.phoneNumber.replaceAll(
        ' ',
        ''
      );
      if (this.selectedPhoneNumber && phoneNumber !== selectedNumber) {
        // Re-check the edited number, as the dialog promises: a fixed invalid
        // number is added, and one edited into an invalid number is skipped.
        const isValid = this.localCSVService.updateAndRevalidateEntry(
          this.selectedPhoneNumber.id,
          phoneNumber!
        );
        if (this.errorToastId) this.toast.dismiss(this.errorToastId);
        this.errorToastId = 0;
        if (isValid) {
          this.toast.success(`${phoneNumber} is valid and will be added.`);
        } else {
          this.errorToastId = this.toast.error(
            `${phoneNumber} is not a valid number and will be skipped. Check the country code and try again.`
          );
        }
      }
      // reset form after successful submission
      this.phoneNumberForm.reset({
        phoneNumber: ' ',
      });
      this.phoneNumberSubmitted = false;
      this.closeUpdateModal();
    }
  }
}
